import React, {
  useState, useEffect, createContext, useMemo,
} from 'react';
import PropTypes from 'prop-types';

export const ResizeContext = createContext(window.innerWidth);


// No matter how many times the user fires the event,
// the attached function will be executed only after
// the specified time once the user stops firing the event.
export const debounce = (func, wait, immediate) => {
  let timeout;
  return (...rest) => {
    const context = this;
    const args = rest;
    const later = () => {
      timeout = null;

      if (!immediate) {
        func.apply(context, args);
      }
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
};


// No matter how many times the user fires the event,
// the attached function will be executed only once
// in a given time interval
export function throttle(cb, delay = 250) {
  let shouldWait = false;

  return (...args) => {
    if (shouldWait) return;
    cb(...args);
    shouldWait = true;
    setTimeout(() => {
      shouldWait = false;
    }, delay);
  };
}

function Resize({ children }) {
  const [windowInnerWidth, setWindowInnerWidth] = useState(0);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const [pageYOffset, setPageYOffset] = useState(0);

  const resize = () => {
    setWindowInnerWidth(window.innerWidth);
  };

  useEffect(() => {
    let oldScrollY = window.scrollY;
    // Ignoring this test as I couldn't mock window.onscroll with react testing library
    // TO DO: Cover this line.
    /* istanbul ignore next */
    const handleScroll = () => {
      window.onscroll = () => {
        setPageYOffset(window?.pageYOffset);
        if (oldScrollY > window.scrollY) {
          setIsScrollingDown(false);
          setIsScrollingUp(true);
        } else {
          setIsScrollingDown(true);
          setIsScrollingUp(false);
        }
        oldScrollY = window.scrollY;
      };
    };
    window.addEventListener('resize', debounce(resize, 250));
    resize();
    window.addEventListener('scroll', throttle(handleScroll, 250));

    return () => {
      window.removeEventListener('resize', debounce(resize));
      window.removeEventListener('scroll', debounce(handleScroll));
    };
  }, [isScrollingDown, isScrollingUp]);

  const context = useMemo(
    () => ({
      isScrollingDown,
      isScrollingUp,
      pageYOffset,
      width: windowInnerWidth,
    }),
    [isScrollingDown, isScrollingUp, pageYOffset, windowInnerWidth],
  );

  return (
    <ResizeContext.Provider value={context}>
      {children}
    </ResizeContext.Provider>
  );
}

Resize.meta = {
  VERSION: '1.0.0',
};

Resize.propTypes = {
  children: PropTypes.node.isRequired,
};

Resize.defaultProps = {};

export default Resize;
